import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from './Ads.styles';
import { Footer } from '@/components/Footer/Footer';
import { TabBar } from '@/components/TabBar/TabBar';
import { PageTopControls } from '@/components/PageTopControls';
import { SearchBaseResults } from '@/components/SearchBaseResults/SearchBaseResults';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useBanner } from '@/hooks/useBanner';
import { CatalogueGrid } from '@/components/CatalogueGrid/CatalogueGrid';
import { SearchPanel } from '@/components/SearchPanel';
import { FiltersButton } from '@/components/Filters/FiltersButton/FiltersButton';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { useServerDataContext } from '@/contexts';
import { InlineFiltersSkeletonLoader } from '@/components/Filters/InlineFilters/InlineFiltersSkeletonLoader';
import { useResponsive } from '@/hooks/useResponsive';
import {
  SearchResults,
  SearchResultsSections,
} from '@/components/SearchResults';
import { UserAdsSkeleton } from '@/features/AdSearch/components/UserAds/UserAdsSkeleton';
import type { AdsSectionLayoutScope } from '@/components/AdsSection/AdsSection.types';
import { ROOT_CATEGORY_SLUG } from '@/constants/common';
import { VIP_ADS_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { buildBreadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs.helpers';
import { useAdsCounter } from '@/components/Filters/AdsCounter/AdsCounter.hooks';
import { AdKind } from '@/api/types/common.types';

const VIP_ONLY_SECTIONS: SearchResultsSections = {
  vipAds: true,
  latestAds: false,
};
const VIP_AND_LATEST_SECTIONS: SearchResultsSections = {
  vipAds: true,
  latestAds: true,
};
const CategoryPathSynchronizer = dynamic(
  () =>
    import('@/components/CategoryPathSynchronizer').then(
      mod => mod.CategoryPathSynchronizer
    ),
  { ssr: false }
);

const UserAds = dynamic(
  () =>
    import('@/features/AdSearch/components/UserAds').then(mod => mod.UserAds),
  { ssr: false, loading: () => <UserAdsSkeleton /> }
);

const InlineFilters = dynamic(
  () =>
    import('@/components/Filters/InlineFilters').then(mod => mod.InlineFilters),
  {
    ssr: false,
    loading: () => <InlineFiltersSkeletonLoader />,
  }
);

export const AdsView = () => {
  const { t } = useTranslation();
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const { isDesktop } = useResponsive();
  const {
    isVipAds,
    isAllCategories,
    isFirstLevel,
    isSubcategories,
    currentCategory = serverCurrentCategory,
    isUserAds,
  } = useCurrentCategory();
  const { adsAmount, loading: isAdsAmountLoading } = useAdsCounter(
    currentCategory,
    isVipAds ? AdKind.Vipped : undefined
  );
  const { topBanner, backgroundBanner, catfishBanner } = useBanner({
    isDesktop,
  });
  const hasCategories = (isAllCategories || isFirstLevel) && !isVipAds;
  const topControlsPosition = usePageTopControlsStore(
    state => state.topControlsPosition
  );
  const [hasNextPage, setHasNextPage] = useState(true);

  const handlePaginationChange = useCallback((nextPageExists: boolean) => {
    setHasNextPage(nextPageExists);
  }, []);

  function getLayoutScope(): AdsSectionLayoutScope {
    if (isSubcategories) return 'subcategory';
    if (isFirstLevel) return 'category';

    return 'all-categories';
  }

  function getHeaderTitle() {
    if (isVipAds) {
      return t('pages.ads.all_vip_ads');
    }

    if (isFirstLevel || isSubcategories) {
      return currentCategory?.name;
    }

    return t('pages.ads.all_categories');
  }

  function getBreadcrumbs() {
    if (isVipAds && currentCategory) {
      return [
        {
          label: t('pages.ads.vip_ads'),
          link: `/${ROOT_CATEGORY_SLUG}/${VIP_ADS_PAGE_SLUG}`,
        },
        ...buildBreadcrumbs(currentCategory, `/${VIP_ADS_PAGE_SLUG}`),
      ];
    }

    return [];
  }

  function renderAdsList() {
    switch (true) {
      case isVipAds: {
        return (
          <SearchResults
            sections={VIP_ONLY_SECTIONS}
            onPaginationChange={handlePaginationChange}
          />
        );
      }
      case isUserAds: {
        return <UserAds />;
      }
      default: {
        return (
          <SearchResults
            sections={VIP_AND_LATEST_SECTIONS}
            onPaginationChange={handlePaginationChange}
          />
        );
      }
    }
  }

  const isTopControlsFixed = topControlsPosition === 'fixed';
  const isFooterVisible = isUserAds || !hasNextPage;
  const layoutScope = getLayoutScope();
  const { Wrapper, Container } = S.getLayoutComponents(layoutScope);

  return (
    <>
      {topBanner}
      {isVipAds && backgroundBanner}
      {catfishBanner}

      <CategoryPathSynchronizer />

      <PageTopControls>
        <S.Header
          title={getHeaderTitle()}
          content={!isDesktop && <SearchPanel />}
          rightAction={!isUserAds && !isVipAds && <FiltersButton />}
        />

        {!isVipAds && !isDesktop && !isUserAds && (
          <SearchBaseResults isHidden={isTopControlsFixed} />
        )}

        {hasCategories && !isDesktop && !isUserAds && (
          <CatalogueGrid isHidden={isTopControlsFixed} />
        )}

        {!isVipAds && !isDesktop && !isUserAds && (
          <InlineFilters isSortVisible={!isTopControlsFixed} />
        )}
      </PageTopControls>

      <Wrapper $isSecondaryBg={isUserAds}>
        <Container $isVipAds={isVipAds}>
          {isVipAds && !isAllCategories && (
            <S.Breadcrumbs
              breadcrumbs={getBreadcrumbs()}
              nestingLvl={isFirstLevel ? 3 : 4}
            />
          )}

          {isVipAds && (
            <S.TitleBox>
              <S.Title>{t('pages.ads.vip_ads')}</S.Title>
              {isAdsAmountLoading ? (
                <S.AdsAmountSkeletonLoader />
              ) : (
                <S.AdsAmount>({adsAmount})</S.AdsAmount>
              )}
            </S.TitleBox>
          )}

          {!isVipAds && isDesktop && !isUserAds && <SearchBaseResults />}

          {hasCategories && isDesktop && !isUserAds && <CatalogueGrid />}

          {renderAdsList()}
        </Container>
      </Wrapper>

      <S.FooterBox $isVisible={isFooterVisible}>
        {isDesktop ? <S.DesktopFooter $isVipPage={isVipAds} /> : <Footer />}
      </S.FooterBox>

      {!isDesktop && <TabBar />}
    </>
  );
};
